import {
  Button,
  CircularProgress,
  Grid,
  Typography,
} from '@mui/material';
import * as Styles from '../../../../../../../assets/styles/appStyles';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';
import style from '../../../departmentStyle';
import DepartmentFilePreview from './DepartmentFilePreview';
import { DepartmentFile } from '../../../../../../../Models/Departments/Details/file';
import { useMemo, useState } from 'react';
import useGetFile from '../../../../../../../hooks/Locations/useFetchFileSasUrl';
import useUI from '../../../../../../../hooks/context/useUI';
import { gridContainer, infoGridItem } from '../../../../../Styles/CustodianAddressDetailsStyles';


function formatFileSize(bytes: number) {
    if (bytes === 0) {
      return '0 Bytes';
    }

    const k = 1024;
    const decimals = 2;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];

    const i = Math.floor(Math.log(bytes) / Math.log(k));
    const fileSize = Number.parseFloat((bytes / Math.pow(k, i)).toFixed(decimals));

    return `${fileSize} ${sizes[i]}`;
  }
  
export default function FilesInfo({
  files,
  onRemoveFile,
}: Readonly<{
  files: DepartmentFile[];
  onRemoveFile?: (id: number) => void;
}>) {
  const { getFileSasUrl } = useGetFile();

  const [filePreview, setFilePreview] = useState<DepartmentFile>();
  const [filePreviewUrl, setFilePreviewUrl] = useState<string>();
  const [downloading, setDownloading] = useState<string[]>([]);
  const [previewing, setPreviewing] = useState<string>('');
  const [openDialog, setOpenDialog] = useState(true);

  const showPreview = false;

  const { showSnackbar } = useUI();
  const groupFiles: { id: string; data: DepartmentFile[] }[] = useMemo(() => {
    const groupedData = files.reduce((acc: any, obj) => {
      const key = obj.fileTypeName;
      if (!acc[key]) {
        acc[key] = [];
      }
      acc[key].push(obj);
      return acc;
    }, {});

    return Object.entries(groupedData).map(([id, data]) => ({
      id,
      data: data as DepartmentFile[],
    }));
  }, [files]);

  const downloadFile = async (file: DepartmentFile) => {
    if (!file.permanentBlobUrl) {
      showSnackbar(["the resource of the file isn't available"], 'error');
      return;
    }
    try {
      setDownloading([...downloading, file.id]);
      const result = await getFileSasUrl(file.permanentBlobUrl);
      setDownloading(downloading.filter((p) => p === file.id));
      window.open(result);
    } catch {
      setDownloading(downloading.filter((p) => p === file.id));
    }
  };

  const preview = async (file: DepartmentFile) => {
    if (!file.permanentBlobUrl) {
      showSnackbar(["the resource of the file isn't available"], 'error');
      return;
    }
    try {
      setOpenDialog(false);
      setPreviewing(file.id);
      const result = await getFileSasUrl(file.permanentBlobUrl);
      setFilePreviewUrl(result);
      setFilePreview(file);
      setPreviewing('');
      setOpenDialog(true);
    } catch {
      setPreviewing('');
      setOpenDialog(false);
    }
  };

  return (
  <>
    <Grid rowSpacing={3} container sx={gridContainer} spacing={2}>
      <Grid container item rowSpacing={3}>
        <Grid item xs={12} sx={infoGridItem}>
          <Typography align="left" sx={Styles.labelTitle}>
            Files
          </Typography>

          <Grid container spacing={2}>
            {groupFiles.length === 0 ? (
              <Grid item xs={12}>
                <Typography sx={{ fontStyle: "italic", color: "gray" }}>
                  No Files
                </Typography>
              </Grid>
            ) : (
              groupFiles.map((p) => (
                <Grid container sx={{ marginTop: "20px" }} key={p.id}>
                  <Grid item xs={12} sx={style.fileGroup}>
                    {p.id}
                  </Grid>
                  {p.data.map((file: DepartmentFile, index) => (
                    <Grid
                      container
                      style={{ width: "48%" }}
                      key={file.id + "" + index}
                      sx={style.file}
                      alignItems={"center"}
                      mr={1}
                    >
                      <Grid item xs={1}>
                        <InsertDriveFileIcon sx={{ fontSize: "30px" }} />
                      </Grid>

                      <Grid item xs={7}>
                        <Typography sx={style.fileName}>
                          {file.fileName}
                        </Typography>
                        <Typography sx={style.fileSize}>
                          {formatFileSize(file.fileSize)}
                        </Typography>
                      </Grid>

                      <Grid item xs={2}>
                        <Button
                          onClick={() => downloadFile(file)}
                          disabled={downloading.includes(file.id)}
                        >
                          {downloading.includes(file.id) ? (
                            <CircularProgress size={20} />
                          ) : (
                            "Download"
                          )}
                        </Button>
                      </Grid>

                      {showPreview && (
                        <Grid item xs={2}>
                          <Button
                            onClick={() => preview(file)}
                            disabled={previewing === file.id}
                          >
                            {previewing === file.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              "Preview"
                            )}
                          </Button>
                        </Grid>
                      )}

                      {onRemoveFile && (
                        <Grid item xs={2}>
                          <Button
                            data-testid="remove-btn"
                            onClick={() => onRemoveFile(+file.id)}
                            disabled={previewing === file.id}
                          >
                            {previewing === file.id ? (
                              <CircularProgress size={20} />
                            ) : (
                              "Remove"
                            )}
                          </Button>
                        </Grid>
                      )}
                    </Grid>
                  ))}
                </Grid>
              ))
            )}
          </Grid>
        </Grid>
      </Grid>
    </Grid>

    {filePreviewUrl && filePreview && (
      <DepartmentFilePreview
        file={filePreview}
        openDialog={openDialog}
        closeDialog={() => setOpenDialog(false)}
        fileUrl={filePreviewUrl}
      />
    )}
  </>
);

}
